import { useEstimatedRIValues } from "hooks/useRIFacPremium";
import React, { useMemo } from "react";
import { TrendingUp, AlertCircle, Loader } from "lucide-react";
import { convertCurrency } from "utils/helpers";

const RiFacPremium: React.FC = () => {
  const { estimatedRIValues, loading, error } = useEstimatedRIValues();

  const { currencyData, totalValue } = useMemo(() => {
    if (!estimatedRIValues || estimatedRIValues.length === 0) {
      return {
        currencyData: [],
        totalValue: 0,
      };
    }

    const currencyMap: Record<string, number> = {};

    estimatedRIValues.forEach((item) => {
      if (item?.currency && item?.total_facultative_premium !== null) {
        const currency = convertCurrency(item.currency);
        currencyMap[currency] =
          (currencyMap[currency] || 0) + item.total_facultative_premium;
      }
    });

    const total = Object.values(currencyMap).reduce(
      (acc, value) => acc + value,
      0
    );

    const currencyArray = Object.entries(currencyMap)
      .map(([currency, amount]) => ({
        currency,
        amount,
        percentage: total > 0 ? (amount / total) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount);

    return {
      currencyData: currencyArray,
      totalValue: total,
    };
  }, [estimatedRIValues]);

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD', // Default, will be overridden per currency
      minimumFractionDigits: 0,
      maximumFractionDigits: amount >= 1000000 ? 1 : 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center text-gray-500">
            <Loader className="animate-spin h-8 w-8 text-green-500 mb-3" />
            <span className="text-sm font-medium">Loading facultative premium data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center text-red-500">
            <AlertCircle className="h-8 w-8 mb-3" />
            <span className="text-sm font-medium">Failed to load premium data</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Facultative Premium by Currency</h2>
          <p className="text-sm text-gray-500 mt-1">Today's market reinsurance placements</p>
        </div>
        {/* {totalValue > 0 && (
          <div className="bg-green-50 rounded-lg px-3 py-2">
            <span className="text-xs font-medium text-green-700">TOTAL</span>
            <p className="text-lg font-semibold text-green-900">
              {new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
              }).format(totalValue)}
            </p>
          </div>
        )} */}
      </div>

      {currencyData.length > 0 ? (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-y-auto flex-1">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                    Allocation
                  </th>
                </tr>
              </thead>
              <tbody>
                {currencyData.map((item, index) => (
                  <tr
                    key={item.currency}
                    className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <span className="inline-block w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3">
                          <span className="text-xs font-medium text-green-800">
                            {item.currency.slice(0, 2)}
                          </span>
                        </span>
                        <span className="font-medium text-gray-900">
                          {item.currency}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-gray-900">
                      {new Intl.NumberFormat(undefined, {
                        style: 'currency',
                        currency: item.currency,
                        currencyDisplay: 'code',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: item.amount >= 1000000 ? 1 : 0
                      }).format(item.amount)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end">
                        <div className="w-full max-w-[120px]">
                          <div className="flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${item.percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right">
                              {item.percentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
          <TrendingUp className="h-10 w-10 mb-3" />
          <p className="font-medium">No premium data available</p>
          <p className="text-sm mt-1">Facultative placements will appear here</p>
        </div>
      )}
    </div>
  );
};

export default RiFacPremium;